import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";

import { useApp } from "../context/AppContext";
import { SERVICE_LOCATION, GET_CLIENT_ACCOUNTS } from "../constants/endpoints";

const AdminHome = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setLoading, showToast } = useApp();

    const { data } = location.state || {};

    const [clientAccounts, setClientAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);

    // ── Fetch all client accounts ─────────────────────────────
    const fetchClientAccounts = useCallback(() => {
        setLoading(true);
        axios
            .get(
                SERVICE_LOCATION + GET_CLIENT_ACCOUNTS,
                { headers: { Authorization: `Bearer ${data.data.token}` } }
            )
            .then(res => {
                if (res.data.isSuccess) {
                    setClientAccounts(res.data.data.clientAccounts || []);
                } else {
                    showToast("error", res.data.errorMessage || "Failed to load client accounts.");
                }
            })
            .catch(() => {
                showToast("error", "Network error. Please try again.");
            })
            .finally(() => setLoading(false));
    }, [data, setLoading, showToast]);

    useEffect(() => { fetchClientAccounts(); }, [fetchClientAccounts]);

    // ── On client change, load dashboard for that client ────
    const handleClientChange = (e) => {
        setSelectedAccount(e.value);
    };

    const handleViewDashboard = () => {
        if (!selectedAccount) return;

        // Updated data with the selected client account
        const updatedData = {
            ...data,
            data: {
                ...data.data,
                clientDetails: {
                    ...data.data.clientDetails,
                    clientAccount: selectedAccount.clientAccount,
                    clientName: selectedAccount.clientName,
                },
            },
        };
        navigate("/home/dashboard", { state: { data: updatedData } });
    };

    // Dropdown option template wit clientName & clientAccount
    const optionTemplate = (option) => (
        <div style={styles.optionRow}>
            <span style={styles.optionName}>{option.clientName}</span>
            <span style={styles.optionCode}>{option.clientAccount}</span>
        </div>
    );

    return (
        <div className="page-enter" style={styles.page}>

            {/* Page heading */}
            <div style={styles.headingRow}>
                <h1 style={styles.heading}>Select a Client</h1>
                <p style={styles.sub}>
                    Choose a client account to view their dashboard and site measures.
                </p>
            </div>

            {/* Selector card */}
            <div style={styles.card}>
                <div style={styles.cardInner}>

                    <div style={styles.fieldWrap}>
                        <label style={styles.label}>Client Account</label>
                        <Dropdown
                            value={selectedAccount}
                            options={clientAccounts}
                            onChange={handleClientChange}
                            optionLabel="clientName"
                            itemTemplate={optionTemplate}
                            placeholder="Search or select a client…"
                            filter
                            filterPlaceholder="Search clients…"
                            style={styles.dropdown}
                            panelStyle={{ fontFamily: "var(--font-sans)", fontSize: 13 }}
                        />
                    </div>

                    <Button
                        label="View Client Sites"
                        disabled={!selectedAccount}
                        onClick={handleViewDashboard}
                        style={{
                            ...styles.submitBtn,
                            ...(selectedAccount ? {} : styles.submitBtnDisabled),
                        }}
                    />
                </div>

                {/* client info on client select */}
                {selectedAccount && (
                    <div style={styles.selectedInfo}>
                        <i className="pi pi-building" style={{ fontSize: 14, color: "#4CAF50" }} />
                        <div>
                            <div style={styles.selectedName}>{selectedAccount.clientName}</div>
                            <div style={styles.selectedCode}>Account: {selectedAccount.clientAccount}</div>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};

const styles = {
    page: {
        maxWidth: 560,
        margin: "40px auto 0",
    },
    headingRow: {
        marginBottom: 28,
        textAlign: "center",
    },
    heading: {
        fontFamily: "var(--font-sans)",
        fontSize: 26,
        fontWeight: 700,
        color: "#111827",
        margin: "0 0 8px 0",
    },
    sub: {
        fontFamily: "var(--font-sans)",
        fontSize: 14,
        color: "#6B7280",
        margin: 0,
    },
    card: {
        background: "#ffffff",
        border: "1px solid #E5E7EB",
        borderRadius: 16,
        padding: "32px 36px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    },
    cardInner: {
        display: "flex",
        flexDirection: "column",
        gap: 20,
    },
    fieldWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
    },
    label: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        fontWeight: 500,
        color: "#4B5563",
    },
    dropdown: {
        width: "100%",
        borderRadius: 8,
        border: "1px solid #D1D5DB",
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        height: 42,
    },
    submitBtn: {
        fontFamily: "var(--font-sans)",
        width: "100%",
        justifyContent: "center",
        height: 44,
        fontSize: 14,
        fontWeight: 600,
        borderRadius: 8,
        background: "#4CAF50",
        borderColor: "#4CAF50",
        color: "#ffffff",
    },
    submitBtnDisabled: {
        background: "#F9FAFB",
        borderColor: "#E5E7EB",
        color: "#9CA3AF",
    },
    selectedInfo: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginTop: 20,
        padding: "12px 16px",
        background: "#F0FDF4",
        border: "1px solid #BBF7D0",
        borderRadius: 8,
    },
    selectedName: {
        fontFamily: "var(--font-sans)",
        fontSize: 14,
        fontWeight: 600,
        color: "#166534",
    },
    selectedCode: {
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        color: "#4B5563",
        marginTop: 2,
    },
    optionRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
    },
    optionName: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        color: "#111827",
        fontWeight: 500,
    },
    optionCode: {
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        color: "#9CA3AF",
    },
};

export default AdminHome;
